import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import bp from 'styled-components-breakpoint';
import noScroll from 'no-scroll';
import { Container, Column, Row } from 'components/Grid';
import { HeaderContext } from '../headerContext';
import MobileNav from './MobileNav';
import { MobileRegionSelector } from './MobileRegionSelector';
import { MobileStudioSelector } from './MobileStudioSelector';
import HeaderMobile from '../HeaderMobile';
import MobilePaneFooter from './MobilePaneFooter';
import MobileSeriesOptions from './MobileSeriesOptions';

const StyledOverlay = styled.div`
  background-color: rgb(0 0 0 / 50%);
  inset: 0;
  display: ${(props) => (props.isOpen ? 'block' : 'none')};
  height: 100%;
  perspective: 1000px;
  position: fixed;
  width: 100%;
  z-index: ${(props) => props.theme.zIndex.overlay};
  ${bp('tablet')`
    display: none;
  `};
`;

StyledOverlay.displayName = 'StyledOverlay';

const PaneContainer = styled.div`
  box-shadow: ${(props) => props.theme.shadows.boxShadow[`2dp`]};
  background-color: ${(props) => props.theme.colors.grayscale.white};
  height: 100%;
  left: 0;
  min-width: 21.5rem;
  perspective: 1000px;
  position: fixed;
  top: 0;
  transform-origin: 0% 0%;
  transition: transform 0.5s cubic-bezier(0.77, 0.2, 0.05, 1);
  transform: ${(props) =>
    props.isOpen ? 'translate3d(0, 0, 0)' : 'translate3d(-101%, 0, 0)'};
  width: 90vw;
  z-index: ${(props) => props.theme.zIndex.modal};
  ${bp('tablet')`
    display: none;
  `};

  /* stylelint-disable-next-line selector-class-pattern */
  &.isOpen {
    transform: translate(0, 0);
  }
`;

const HeaderOptions = styled.div`
  align-items: center;
  background-color: ${(props) => props.theme.colors.grayscale.white};
  border-top: 1px solid ${(props) => props.theme.colors.grayscale.gray3};
  box-sizing: border-box;
  display: flex;
  height: 7.1875rem;
  left: 0;
  min-width: 21.5rem;
  padding: 1rem 0;
  position: relative;
  top: 0;
  width: 90vw;
  ${bp('tablet')`
    width: 75vw;
  `}
`;

PaneContainer.displayName = 'PaneContainer';

const StyledMobileHeader = styled(HeaderMobile)`
  border-bottom: 1px solid ${(props) => props.theme.colors.grayscale.gray1};
`;

const ContentContainer = styled(Container)`
  -webkit-overflow-scrolling: touch;
  height: calc(100% - 10.6875rem);
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  padding: 0;
`;

const StyledRow = styled(Row)`
  display: block;
`;

function MobilePane({ isOpen, menuButtonClick, showBookBikeButton }) {
  const headerContext = useContext(HeaderContext);
  const { currentRegion, onRegionChange, onStudioSelect } = headerContext;
  const [activeState, setActiveState] = useState('nav');
  const [activeRegion, setActiveRegion] = useState(currentRegion);

  if (isOpen) {
    noScroll.on();
  } else {
    noScroll.off();
  }

  // update state when the activeRegion is updated
  useEffect(() => {
    setActiveState('nav');
  }, [activeRegion]);

  // update activeRegion state when currentRegion value changes
  useEffect(() => {
    if (currentRegion.id !== activeRegion.id) {
      setActiveRegion(currentRegion);
    }
  }, [currentRegion]);

  /**
   * sets the active state of the mobile header based on the region button click.
   */
  const handleRegionButtonClick = () => {
    setActiveState(activeState === 'region' ? 'nav' : 'region');
  };

  const handleStudioButtonClick = () => {
    setActiveState(activeState === 'studios' ? 'nav' : 'studios');
  };

  /**
   * on close set the active state to nav
   */
  const handleClose = () => {
    setActiveState('nav');
    menuButtonClick();
  };

  /**
   * handles the active region based on a region change
   *
   * @param {object} the selected region
   */
  const handleRegionChange = (region) => {
    setActiveRegion(region);

    if (typeof onRegionChange === 'function') {
      onRegionChange(region);
      return;
    }

    console.error(
      'the onRegionChange method passed to the Mobile Pane is not a valid function'
    );
  };

  const renderPaneBody = () => {
    switch (activeState) {
      case 'region':
        return (
          <ContentContainer py="2rem">
            <StyledRow>
              <Column ml="0">
                <MobileRegionSelector
                  id="mobile-studios-list"
                  selectedItem={activeRegion}
                  onClick={handleRegionChange}
                />
              </Column>
            </StyledRow>
          </ContentContainer>
        );
      case 'studios':
        return (
          <ContentContainer px="0">
            <StyledRow>
              <Column width={1}>
                <MobileStudioSelector onClick={onStudioSelect} />
              </Column>
            </StyledRow>
          </ContentContainer>
        );
      default:
        return (
          <ContentContainer py="2rem">
            <StyledRow>
              <Column>
                <MobileNav />
              </Column>
            </StyledRow>
          </ContentContainer>
        );
    }
  };

  const renderPaneHeader = () => {
    if (activeState === 'studios') {
      return (
        <HeaderOptions>
          <MobileSeriesOptions
            activeState={activeState}
            activeRegion={activeRegion}
            regionClick={handleRegionButtonClick}
            studiosClick={handleStudioButtonClick}
          />
        </HeaderOptions>
      );
    }

    return (
      <StyledMobileHeader
        id="mobile-pane-header"
        isOpen={isOpen}
        menuButtonClick={handleClose}
        showSecondaryButton={false}
      />
    );
  };

  return (
    <>
      <StyledOverlay isOpen={isOpen} />
      <PaneContainer isOpen={isOpen}>
        {renderPaneHeader()}
        {renderPaneBody()}
        <MobilePaneFooter
          activeState={activeState}
          activeRegion={activeRegion}
          regionClick={handleRegionButtonClick}
          studiosClick={handleStudioButtonClick}
          showBookBikeButton={showBookBikeButton}
        />
      </PaneContainer>
    </>
  );
}

MobilePane.displayName = 'MobilePane';

MobilePane.propTypes = {
  isOpen: PropTypes.bool,
  menuButtonClick: PropTypes.func.isRequired,
  showBookBikeButton: PropTypes.bool,
};

MobilePane.defaultProps = {
  isOpen: false,
  showBookBikeButton: true,
};

export default MobilePane;
